import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Input,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import { Container, MessageDrawer } from '../../components'
import { useCompany, useUser } from '../../hooks'
import { NatPropListTable } from './NatPropListTable'
import {
    ApartmentVendor,
    Company,
    CompanyStatus,
    IdBoolMap,
    ListChannel,
    ListVendor,
    PlanStatus,
    Service,
} from '../../models'
import { axiosInstance, statesList } from '../../helpers'
import { Routes } from '../../navigation/config/routes'
import { useHistory } from 'react-router-dom'
import { useMultiSelectStyles } from '../../styles'
import { BuyPropertyModal } from './BuyPropertyModal'
import { PropertyDetailModal } from './PropertyDetailModal'

export interface ApartmentVendorChannel {
    channel: ListChannel
    apartment?: Company
    vendor?: ListVendor
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 104px)',
        width: '100%',
        backgroundColor: theme.palette.background.default,
    },
    header: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2, 3),
        display: 'flex',
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: '20px',
        fontWeight: 700,
        letterSpacing: '-0.02em',
    },
    searchFilterContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
    },
    searchField: {
        flex: 1,
        minWidth: 200,
    },
    mainContent: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(2, 3),
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    upgradeContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
        textAlign: 'center',
    },
}))

export const NationalPropertyList = () => {
    const classes = useStyles()
    const multiSelectClasses = useMultiSelectStyles()
    const history = useHistory()

    const [searchText, setSearchText] = useState('')
    const [loadingState, setLoadingState] = useState(true)
    const [selectedProperty, setSelectedProperty] = useState<
        Company | undefined
    >()
    const [serviceList, setServiceList] = useState<Service[]>([])
    const [vendorPlan, setVendorPlan] = useState<PlanStatus | null>(null)
    const [unlockedPropertyMap, setUnlockedPropertyMap] = useState<IdBoolMap>(
        {},
    )
    const [goodServiceList, setGoodServiceList] = useState<
        Service[] | undefined
    >([])
    const [selectedLocations, setSelectedLocations] = useState<string[]>([])
    const [messageDrawerOpen, setMessageDrawerOpen] = useState(false)
    const [apartmentVendorChannels, setApartmentVendorChannels] = useState<
        ApartmentVendorChannel[]
    >([])
    const [openBuyPropertyModal, setOpenBuyPropertyModal] = useState(false)
    const [openPropertyDetail, setOpenPropertyDetail] = useState(false)

    const { workspaceUser } = useUser()
    const { getApartmentList, apartmentList } = useCompany()

    useEffect(() => {
        const apartmentListPromise = getApartmentList({
            params: { all_apartments: true },
        })
        const vendorPromise = axiosInstance.get(
            `company/vendor/${workspaceUser?.active_workspace.id}`,
        )
        const channelListPromise = axiosInstance.get('messaging/channel/', {
            params: { channel_type: 'APARTMENT_VENDOR' },
        })
        const apartmentVendorPromise = axiosInstance.get(
            'company/apartment_vendors/',
        )
        const VENDOR_INDEX = 1
        const CHANNEL_INDEX = 2
        const APARTMENT_VENDOR_INDEX = 3

        Promise.all([
            apartmentListPromise,
            vendorPromise,
            channelListPromise,
            apartmentVendorPromise,
        ])
            .then((values) => {
                setServiceList(values[VENDOR_INDEX].data.services)
                setVendorPlan(values[VENDOR_INDEX].data.plan)
                const tempApartmentVendorChannels: ApartmentVendorChannel[] = []
                values[CHANNEL_INDEX].data.map((channel: ListChannel) => {
                    tempApartmentVendorChannels.push({ channel })
                })
                values[APARTMENT_VENDOR_INDEX].data.map(
                    (apartmentVendor: ApartmentVendor) => {
                        tempApartmentVendorChannels.forEach(
                            (aptVendorChannel) => {
                                if (
                                    aptVendorChannel.channel.id ===
                                    apartmentVendor.channel_id
                                ) {
                                    aptVendorChannel.apartment =
                                        apartmentVendor.apartment
                                    aptVendorChannel.vendor =
                                        apartmentVendor.vendor
                                }
                            },
                        )
                    },
                )
                setApartmentVendorChannels(tempApartmentVendorChannels)
            })
            .catch((e) => console.log(e))
            .finally(() => setLoadingState(false))
    }, [])

    const apartmentFilter = (property: Company) => {
        const nameMatch = property.name
            .toLowerCase()
            .includes(searchText.toLowerCase())
        const locationMatch =
            selectedLocations.length === 0 ||
            selectedLocations.includes(property.state)
        return (
            nameMatch &&
            locationMatch &&
            property.status === CompanyStatus.ACTIVE
        )
    }

    const allPropertiesUnlockedMap = useMemo(
        () =>
            apartmentList.reduce<IdBoolMap>(
                (acc, p) => ({ ...acc, [p.id]: true }),
                {},
            ),
        [apartmentList],
    )

    const filteredProperties = apartmentList.filter(apartmentFilter)

    return (
        <Box className={classes.root}>
            {/* Header - matches ContractManager */}
            <Box className={classes.header}>
                <Typography className={classes.headerTitle}>
                    Property List
                </Typography>
            </Box>

            {/* Search & filter bar - matches ProspectsSearchAndFilter */}
            <Box className={classes.searchFilterContainer}>
                <TextField
                    placeholder="Search by property name..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl
                    className={multiSelectClasses.formControl}
                    size="small"
                    style={{ minWidth: 180 }}
                >
                    <InputLabel id="property-location-label">State</InputLabel>
                    <Select
                        labelId="property-location-label"
                        id="mutiple-chip-property-location"
                        multiple
                        value={selectedLocations}
                        MenuProps={{
                            getContentAnchorEl: () =>
                                (null as unknown) as Element,
                        }}
                        onChange={(
                            event: React.ChangeEvent<{
                                value: unknown
                            }>,
                        ) => {
                            setSelectedLocations(event.target.value as string[])
                        }}
                        input={
                            <Input id="select-multiple-chip-property-location" />
                        }
                        renderValue={(selected) => (
                            <div className={multiSelectClasses.chips}>
                                {(selected as string[]).map((value) => {
                                    const vnd = statesList.find(
                                        (s) => s === value,
                                    )
                                    return (
                                        <Chip
                                            key={`LOCATION_CHIP_${vnd ?? -1}`}
                                            label={vnd ?? 'unknown'}
                                            className={multiSelectClasses.chip}
                                            size="small"
                                        />
                                    )
                                })}
                            </div>
                        )}
                    >
                        {statesList.map((state) => (
                            <MenuItem
                                key={`SLOCATION_MENU_ITEM_${state}`}
                                value={state}
                            >
                                {state}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" color="textSecondary">
                    {filteredProperties.length} propert
                    {filteredProperties.length === 1 ? 'y' : 'ies'}
                </Typography>
            </Box>

            {/* Main content - matches ContractManager mainContent */}
            <Box className={classes.mainContent}>
                {loadingState ? (
                    <Box className={classes.loadingContainer}>
                        <CircularProgress />
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 16 }}
                        >
                            Loading properties…
                        </Typography>
                    </Box>
                ) : vendorPlan !== PlanStatus.PREMIUM ? (
                    <Box className={classes.upgradeContainer}>
                        <Typography variant="h6" gutterBottom>
                            Premium required
                        </Typography>
                        <Typography
                            variant="body1"
                            color="textSecondary"
                            style={{ marginBottom: 24, maxWidth: 400 }}
                        >
                            Upgrade to Premium to access the National Property
                            List and get top-of-the-list placement and featured
                            vendor status.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => history.push(Routes.vendor.settings)}
                        >
                            Upgrade to Premium
                        </Button>
                    </Box>
                ) : (
                    <NatPropListTable
                        properties={filteredProperties}
                        setSelectedProperty={setSelectedProperty}
                        vendorPlan={vendorPlan}
                        setMessageDrawerOpen={setMessageDrawerOpen}
                        apartmentVendorChannels={apartmentVendorChannels}
                        setApartmentVendorChannels={setApartmentVendorChannels}
                        unlockedPropertyMap={allPropertiesUnlockedMap}
                        setOpenBuyProperty={setOpenBuyPropertyModal}
                        setOpenPropertyDetail={setOpenPropertyDetail}
                    />
                )}
            </Box>

            <MessageDrawer
                title={`${selectedProperty?.name}`}
                open={messageDrawerOpen}
                onClose={() => setMessageDrawerOpen(false)}
            />
            {selectedProperty && (
                <BuyPropertyModal
                    open={openBuyPropertyModal}
                    handleClose={() => setOpenBuyPropertyModal(false)}
                    property={selectedProperty}
                    unlockedPropertyMap={unlockedPropertyMap}
                    setUnlockedPropertyMap={setUnlockedPropertyMap}
                />
            )}
            {selectedProperty && (
                <PropertyDetailModal
                    open={openPropertyDetail}
                    property={selectedProperty}
                    handleClose={() => setOpenPropertyDetail(false)}
                />
            )}
        </Box>
    )
}
